import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api } from './api'

export type Role = 'admin' | 'rep' | 'spec' | 'buyer'
export type Me = {
  user: { id: string; name: string; email: string; role: Role; org_id: string; subscriber_id?: string | null }
  org: { id: string; name: string; slug: string; timezone: string }
  unread: number
  open_leads: number
  can_view_as: boolean
  must_change_password?: boolean
  viewing_as: { real_name: string; real_role: Role; client_preview?: boolean } | null
}
export type Integration = { adapter: string; live: boolean; note?: string; screen_model?: string; extract_model?: string }
export type InstallConfig = { name: string; brand: string; demoData: boolean; bundle: string; integrations: Record<string, Integration> }

type Status = 'checking' | 'signed_out' | 'signed_in'

const Ctx = createContext<{ status: Status; me: Me | null; config: InstallConfig | null; refresh: () => Promise<void> }>({
  status: 'checking', me: null, config: null, refresh: async () => {},
})

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('checking')
  const [me, setMe] = useState<Me | null>(null)
  const [config, setConfig] = useState<InstallConfig | null>(null)

  const refresh = useCallback(async () => {
    try {
      const c = await api<InstallConfig>('/api/config')
      setConfig(c)
      applyBrand(c.brand)
    } catch { /* config is decoration; the session call decides */ }
    try {
      const m = await api<Me>('/api/me')
      setMe(m)
      document.title = `${m.org.name} leads`
      setStatus('signed_in')
    } catch {
      setMe(null)
      setStatus('signed_out')
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])
  useEffect(() => {
    const onEnded = () => { setMe(null); setStatus('signed_out') }
    window.addEventListener('session:ended', onEnded)
    return () => window.removeEventListener('session:ended', onEnded)
  }, [])

  return <Ctx.Provider value={{ status, me, config, refresh }}>{children}</Ctx.Provider>
}

export const useSession = () => useContext(Ctx)
export const isAdmin = (me: Me | null) => me?.user.role === 'admin' || me?.user.role === 'spec'

/**
 * The install's brand for pages outside sign-in (invite and reset links), so a
 * client's first look at the platform is Handpicked's, not the default skin.
 */
export function useInstallBrand() {
  const [brand, setBrand] = useState<string | null>(null)
  useEffect(() => {
    api<InstallConfig>('/api/config').then((c) => { applyBrand(c.brand); setBrand(c.brand) }).catch(() => setBrand(''))
  }, [])
  return brand
}

function applyBrand(brand: string) {
  document.documentElement.dataset.brand = brand
  applyBrandChrome(brand)
}

/** The browser's own chrome for the install's brand: tab icon, phone status bar colour, and the home-screen app's name and icon. */
const CHROME: Record<string, { icon: string; touch: string; theme: string; scheme?: 'light' | 'dark'; manifest?: string; title?: string }> = {
  // Handpicked is cream-based in every setting: no dark mode, whatever the device prefers.
  handpicked: { icon: '/favicon-handpicked.png', touch: '/apple-touch-icon-handpicked.png', theme: '#F8F3EA', scheme: 'light', manifest: '/manifest-handpicked.webmanifest', title: 'Handpicked' },
}
function applyBrandChrome(brand: string) {
  const c = CHROME[brand]
  if (!c) return
  if (c.scheme) document.documentElement.dataset.theme = c.scheme
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  icon?.setAttribute('type', 'image/png'); icon?.setAttribute('href', c.icon)
  document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')?.setAttribute('href', c.touch)
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', c.theme)
  // Added to a home screen, the app is called this and opens at "/", which takes each login to its own home.
  if (c.manifest) document.querySelector<HTMLLinkElement>('link[rel="manifest"]')?.setAttribute('href', c.manifest)
  if (c.title) document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', c.title)
}
